import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner, Modal, ModalContent, ModalBody } from '@heroui/react';
import { FaRegClock, FaCoins, FaCheck, FaPlay, FaVideo, FaSliders, FaBolt } from 'react-icons/fa6';
import ChipInput from '../primitives/ChipInput.jsx';
import Segmented from '../primitives/Segmented.jsx';
import VideoCard from '../VideoCard.jsx';
import { Field } from '../primitives/bits.jsx';
import autoDubService from '../../../services/autoDub/autoDubService.js';
import { PLAYLISTS, STAGES } from '../../../services/autoDub/mockData.js';
import { matchVideos, aggregateCost, hasAnyFilter, fmtViews } from '../../../services/autoDub/cost.js';

const inputCls =
	'w-full bg-white border-[1.5px] border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/10 transition placeholder:text-slate-400';

/* Step 2 — sticky filter panel + live results grid with per-video cost.
   Filters AND-combine; the summary bar and cards update on every change. */
export default function StepVideos({ state, set, editing = false }) {
	const f = state.filters;
	const setF = (patch) => set({ filters: { ...f, ...patch } });
	const isExisting = state.type === 'backfill';
	// Edit picker (inclusion model) only kicks in for a hydrated backfill edit.
	// Create / duplicate keep the exclusion model below.
	const editMode = editing && isExisting;
	// Watch mode only: 'all' dubs every new upload (default); 'filtered' reveals the
	// rules. In 'all' the filter fields are greyed out and their values are cleared,
	// so the automation ships empty filters (which the backend matches as "everything").
	const filterMode = state.filterMode || 'all';
	const filtersDisabled = !isExisting && filterMode === 'all';

	const { data: videos = [], isLoading } = useQuery({
		queryKey: ['autoDubVideos', state.source?.id],
		queryFn: () => autoDubService.listVideos(state.source?.id),
		enabled: !!state.source,
	});

	const matched = useMemo(() => matchVideos(videos, f), [videos, f]);

	// Videos already in the automation (edit only): id → {stage, removable}. The
	// ones whose dub has started (removable === false) are locked — always kept.
	const existingById = useMemo(
		() => new Map((editMode ? state.existingVideos || [] : []).map(e => [e.id, e])),
		[editMode, state.existingVideos],
	);
	const lockedIds = useMemo(
		() => new Set([...existingById.values()].filter(e => !e.removable).map(e => e.id)),
		[existingById],
	);

	// ── Per-video selection ──
	// Create / duplicate: EXCLUSION model — the draft tracks un-checked ids;
	//   everything matched is selected, newly-matching videos default selected.
	// Edit: INCLUSION model — selectedIds is the source of truth (seeded from the
	//   automation's existing videos). New videos default UNSELECTED; locked videos
	//   are always part of the set.
	const deselected = useMemo(() => new Set(state.deselectedIds || []), [state.deselectedIds]);
	const selectedSet = useMemo(() => {
		const s = new Set(state.selectedIds || []);
		lockedIds.forEach(id => s.add(id));
		return s;
	}, [state.selectedIds, lockedIds]);

	// In edit mode also show existing videos that fall outside the current filters,
	// so the user always sees (and can keep/remove) what the automation holds.
	const displayVideos = useMemo(() => {
		if (!editMode) return matched;
		const seen = new Set(matched.map(v => v.id));
		const extras = videos.filter(v => existingById.has(v.id) && !seen.has(v.id));
		return [...matched, ...extras];
	}, [editMode, matched, videos, existingById]);

	const isSelected = (id) => (editMode ? selectedSet.has(id) : !deselected.has(id));
	// `selected` = the shown videos that are ticked — drives the cost preview, the
	// stat number and the select-all toggle. The *saved* set (`selectionIds`) is the
	// real source of truth: in edit it's state.selectedIds ∪ locked, NOT pruned to
	// the visible grid, so a newly-added video survives a later filter change.
	const selected = useMemo(
		() => (isExisting ? displayVideos.filter(v => isSelected(v.id)) : matched),
		[isExisting, editMode, displayVideos, matched, selectedSet, deselected], // eslint-disable-line react-hooks/exhaustive-deps
	);
	// Create/duplicate only: the saved set is matched-minus-deselected. In edit,
	// state.selectedIds is owned directly by the toggle handlers (seeded from the
	// automation's existing videos, which already include the locked ones), so we
	// never recompute it here — doing so would feed back into selectedSet and loop.
	const selectionIds = useMemo(
		() => (isExisting && !editMode ? matched.filter(v => !deselected.has(v.id)).map(v => v.id) : []),
		[isExisting, editMode, matched, deselected],
	);
	const allSelected = isExisting && displayVideos.length > 0 && selected.length === displayVideos.length;

	const toggle = (id) => {
		if (editMode) {
			if (lockedIds.has(id)) return; // started dubs can't be removed
			set(d => {
				const next = new Set(d.selectedIds || []);
				next.has(id) ? next.delete(id) : next.add(id);
				return { selectedIds: [...next] };
			});
			return;
		}
		set(d => {
			const next = new Set(d.deselectedIds || []);
			next.has(id) ? next.delete(id) : next.add(id);
			return { deselectedIds: [...next] };
		});
	};
	// Select-all / deselect-all. Edit: select all shown (incl. locked) / keep only
	// locked. Create: clear deselection for the matched set / deselect all matched.
	const selectAll = () => {
		if (editMode) { set({ selectedIds: displayVideos.map(v => v.id) }); return; }
		set(d => {
			const matchedIds = new Set(matched.map(v => v.id));
			return { deselectedIds: (d.deselectedIds || []).filter(id => !matchedIds.has(id)) };
		});
	};
	const deselectAll = () => {
		if (editMode) { set({ selectedIds: [...lockedIds] }); return; }
		set({ deselectedIds: matched.map(v => v.id) });
	};

	// Surface the selected set + count to the builder (drives Step-2 validity and
	// the save payload). Transient _selectedCount key; stripped from the payload.
	useEffect(() => {
		if (!isExisting) return;
		// Edit: only publish the count — selectedIds is already maintained by the
		// handlers. Create: publish the derived selection + count for the payload.
		if (editMode) set({ _selectedCount: selectedSet.size });
		else set({ selectedIds: selectionIds, _selectedCount: selectionIds.length });
	}, [isExisting, editMode, selectionIds, selectedSet]); // eslint-disable-line react-hooks/exhaustive-deps

	const { perLang, billableMin } = aggregateCost(selected, 1);
	// Watch mode shows a forward-looking, per-upload estimate instead of a grid of
	// past videos. It's averaged across the channel's recent uploads (not the filtered
	// subset), so the figure stays stable and still renders even when a narrow rule
	// matches none of the recent uploads — the fallback only shows if the channel is
	// unreadable / has no uploads at all.
	const channelCost = useMemo(() => aggregateCost(videos, 1), [videos]);
	const sampleCount = videos.length;
	const avgCreditsPerLang = sampleCount ? Math.round(channelCost.perLang / sampleCount) : 0;
	const avgUploadMin = sampleCount ? Math.max(1, Math.round(channelCost.billableMin / sampleCount)) : 0;
	const anyFilter = hasAnyFilter(f);
	const activeCount = (f.titleKeywords.length ? 1 : 0) + (f.tags.length ? 1 : 0) + (f.playlists.length ? 1 : 0)
		+ (f.dateFrom ? 1 : 0) + (f.dateTo ? 1 : 0) + (f.minViews ? 1 : 0) + (f.durMin ? 1 : 0) + (f.durMax ? 1 : 0)
		+ (f.shorts !== 'include' ? 1 : 0);
	const [preview, setPreview] = useState(null);

	const togglePlaylist = (p) =>
		setF({ playlists: f.playlists.includes(p) ? f.playlists.filter(x => x !== p) : [...f.playlists, p] });

	const clearAll = () => setF({
		titleKeywords: [], tags: [], playlists: [], dateFrom: '', dateTo: '',
		minViews: '', durMin: '', durMax: '', shorts: 'include',
	});

	// Watch-mode "all vs filtered" switch. Picking "all" wipes any typed rules so the
	// automation truly dubs everything; picking "filtered" just unlocks the fields.
	const applyMode = (mode) => {
		if (mode === 'all') {
			set({
				filterMode: 'all',
				filters: {
					...f, titleKeywords: [], tags: [], playlists: [], dateFrom: '', dateTo: '',
					minViews: '', durMin: '', durMax: '', shorts: 'include',
				},
			});
		} else {
			set({ filterMode: 'filtered' });
		}
	};

	return (
		<div>
			{/* scoped keyframes for the hero's play-pulse motif */}
			<style>{`@keyframes adPulse{0%{transform:scale(.9);opacity:.55}100%{transform:scale(1.9);opacity:0}}`}</style>

			{/* gradient hero */}
			<div
				className="relative overflow-hidden rounded-3xl mb-7 px-6 py-7 md:px-9 md:py-9"
				style={{ background: 'linear-gradient(120deg,#2a0a4f 0%,#5A0AE0 52%,#8C2CE2 100%)' }}
			>
				<div className="pointer-events-none absolute -top-16 -right-10 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
				<div className="pointer-events-none absolute -bottom-24 left-1/4 w-56 h-56 rounded-full bg-fuchsia-400/20 blur-3xl" />

				{/* pulsing play motif */}
				<div className="pointer-events-none absolute top-1/2 -translate-y-1/2 right-9 hidden sm:grid place-items-center w-16 h-16">
					<span className="absolute w-14 h-14 rounded-full bg-white/25" style={{ animation: 'adPulse 2.2s ease-out infinite' }} />
					<span className="absolute w-14 h-14 rounded-full bg-white/25" style={{ animation: 'adPulse 2.2s ease-out infinite', animationDelay: '1.1s' }} />
					<span className="relative w-12 h-12 rounded-full bg-white grid place-items-center text-primary shadow-lg"><FaPlay size={16} className="ml-0.5" /></span>
				</div>

				<div className="relative">
					<span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white">
						<FaVideo size={13} /> Step 2 · Videos · {isExisting ? 'Existing' : 'Future'}
					</span>
					<h2 className="mt-4 text-[26px] md:text-[30px] font-bold text-white tracking-tight leading-tight">{isExisting ? 'Pick the videos to dub' : 'Which uploads should we pick up?'}</h2>
					<p className="mt-2 text-[14.5px] text-white/80 leading-relaxed max-w-xl">
						{isExisting
							? 'Filter to narrow your uploads, then tick the ones you want dubbed.'
							: 'Set the rules once. Every future upload that matches gets dubbed. Below, your recent uploads show how the rules would apply.'}
					</p>
				</div>
			</div>

			<div className="grid gap-6 items-start" style={{ gridTemplateColumns: 'minmax(0,330px) 1fr' }}>
				{/* filters */}
				<div className="bg-white rounded-3xl border border-slate-200 shadow-small p-5 flex flex-col gap-[18px] sticky top-2">
					<div className="flex items-center justify-between -mb-1">
						<div className="flex items-center gap-2 text-[14.5px] font-bold text-slate-800"><FaSliders size={14} className="text-primary" /> Filters</div>
						{activeCount > 0 && <span className="text-[11px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">{activeCount} active</span>}
					</div>
					<div className="h-px bg-slate-100 -mx-5" />

					{/* Watch mode: choose "dub everything" (default) vs "only matching a filter".
					   Hidden for the backfill picker, which is always a hand-pick. */}
					{!isExisting && (
						<div className="flex flex-col gap-2">
							{[
								['all', 'Dub all new videos', 'Every new upload gets dubbed automatically.'],
								['filtered', 'Only videos matching a filter', 'Set the rules below — only matching uploads get dubbed.'],
							].map(([val, label, desc]) => {
								const on = filterMode === val;
								return (
									<button
										key={val}
										type="button"
										onClick={() => applyMode(val)}
										aria-pressed={on}
										className={`text-left rounded-2xl border-[1.5px] px-3.5 py-3 transition ${on ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'}`}
									>
										<div className="flex items-start gap-2.5">
											<span className={`mt-0.5 w-4 h-4 rounded-full border-[1.5px] grid place-items-center flex-none ${on ? 'border-primary' : 'border-slate-300'}`}>
												{on && <span className="w-2 h-2 rounded-full bg-primary" />}
											</span>
											<span className="min-w-0">
												<span className={`block text-[13.5px] font-semibold ${on ? 'text-primary' : 'text-slate-800'}`}>{label}</span>
												<span className="block text-[11.5px] text-slate-500 mt-0.5 leading-snug">{desc}</span>
											</span>
										</div>
									</button>
								);
							})}
						</div>
					)}

					{/* Filter fields — greyed out & non-interactive while "Dub all" is selected. */}
					<div className={`flex flex-col gap-[18px] transition ${filtersDisabled ? 'opacity-60 pointer-events-none select-none' : ''}`} aria-disabled={filtersDisabled}>
					<Field label="Title or description contains" hint="Any of these words — like you'd search your own channel.">
						<ChipInput value={f.titleKeywords} onChange={v => setF({ titleKeywords: v })} placeholder="e.g. interview, review — Enter" />
					</Field>

					<Field label="Tags" hint="Videos carrying any of these tags.">
						<ChipInput value={f.tags} onChange={v => setF({ tags: v })} placeholder="e.g. tech, news — Enter" />
					</Field>

					<Field label="Playlists">
						<div className="flex gap-1.5 flex-wrap">
							{PLAYLISTS.map(p => {
								const on = f.playlists.includes(p);
								return (
									<button key={p} type="button" onClick={() => togglePlaylist(p)}
										className={`inline-flex items-center gap-1.5 text-[13px] px-3.5 py-2 rounded-full transition ${on ? 'bg-primary/10 text-primary font-semibold' : 'bg-slate-100 text-slate-600 font-medium'}`}>
										{on && <FaCheck size={11} />}{p}
									</button>
								);
							})}
						</div>
					</Field>

					<Field label={isExisting ? 'Uploaded between' : 'Only uploads after'}>
						<div className="flex gap-2 items-center flex-wrap">
							<input type="date" className={inputCls} style={{ flex: 1, minWidth: 120 }} value={f.dateFrom} onChange={e => setF({ dateFrom: e.target.value })} />
							{isExisting && (
								<>
									<span className="text-slate-400 text-sm">to</span>
									<input type="date" className={inputCls} style={{ flex: 1, minWidth: 120 }} value={f.dateTo} onChange={e => setF({ dateTo: e.target.value })} />
								</>
							)}
						</div>
					</Field>

					<Field label="Minimum views" hint="Skip videos that haven't taken off yet.">
						<input type="number" className={inputCls} placeholder="e.g. 100000" value={f.minViews} onChange={e => setF({ minViews: e.target.value })} />
					</Field>

					<Field label="Video length (minutes)">
						<div className="flex gap-2 items-center">
							<input type="number" className={inputCls} placeholder="Min" value={f.durMin} onChange={e => setF({ durMin: e.target.value })} />
							<span className="text-slate-400">–</span>
							<input type="number" className={inputCls} placeholder="Max" value={f.durMax} onChange={e => setF({ durMax: e.target.value })} />
						</div>
					</Field>

					<Field label="Shorts or long-form?">
						<Segmented
							options={[['include', 'Both'], ['exclude', 'Long-form'], ['only', 'Shorts']]}
							value={f.shorts}
							onChange={v => setF({ shorts: v })}
						/>
					</Field>

					{anyFilter && (
						<button type="button" onClick={clearAll} className="text-[13px] font-semibold text-primary text-left">Clear all filters</button>
					)}
					</div>
				</div>

				{/* live results */}
				<div>
					{!isExisting ? (
						/* Future (watch) mode: no back-catalogue grid — matching happens on
						   future uploads, so we show a forward-looking per-upload estimate. */
						isLoading ? (
							<div className="bg-white rounded-3xl border border-slate-200 shadow-small p-12 grid place-items-center"><Spinner color="primary" /></div>
						) : (
							<div className="flex flex-col gap-3.5">
								<div className="bg-white rounded-3xl border border-slate-200 shadow-small p-6">
									<div className="flex items-start gap-3.5">
										<div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary grid place-items-center flex-none"><FaBolt size={18} /></div>
										<div>
											<div className="text-[15.5px] font-bold text-slate-800">Dubbing runs on autopilot</div>
											<p className="text-[13px] text-slate-500 mt-1 leading-relaxed">
												{filterMode === 'all'
													? 'Every new upload on this channel gets dubbed automatically the moment it’s published.'
													: 'New uploads that match your rules get dubbed automatically the moment they’re published — nothing to pick by hand.'}
											</p>
										</div>
									</div>
								</div>
								<div className="rounded-3xl border border-amber-200/70 shadow-small p-6" style={{ background: 'linear-gradient(135deg,#fffbeb 0%,#ffffff 62%)' }}>
									<div className="flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-wide text-amber-700/80">
										<FaCoins size={12} /> Estimated cost per new upload
									</div>
									{sampleCount ? (
										<>
											<div className="mt-3 flex items-end gap-2">
												<span className="text-[34px] font-extrabold text-slate-800 leading-none">≈ {avgCreditsPerLang.toLocaleString()}</span>
												<span className="text-[13px] font-semibold text-slate-500 mb-1">credits / language</span>
											</div>
											<div className="mt-2.5 flex items-center gap-1.5 text-[12.5px] text-slate-500">
												<FaRegClock size={12} className="text-slate-400" />
												Typical {avgUploadMin}-min upload matching your rules.
											</div>
											<div className="mt-1 text-[12px] text-slate-400">Final cost scales with each video’s length × the languages you pick next.</div>
										</>
									) : (
										<div className="mt-3 text-[13px] text-slate-500">We’ll estimate the cost once we can read this channel’s recent uploads.</div>
									)}
								</div>
							</div>
						)
					) : (
					<>
					{/* live stats strip — parent never wraps, so the select-all action stays
					   pinned to the right; only the stat tiles wrap internally when cramped */}
					<div className="bg-white rounded-3xl border border-slate-200 shadow-small px-5 py-4 mb-3.5 flex items-center justify-between gap-4 flex-nowrap">
						<div className="flex items-center gap-x-5 gap-y-3 flex-wrap min-w-0">
							<div>
								<div className="text-[22px] font-bold leading-none">
									<span className={isExisting && selected.length > 0 ? 'text-primary' : 'text-slate-800'}>{isExisting ? selected.length : matched.length}</span>
									{isExisting && <span className="text-[13px] font-semibold text-slate-400"> / {displayVideos.length}</span>}
								</div>
								<div className="text-[12px] text-slate-500 mt-1">{isExisting ? 'videos selected' : 'recent uploads matched'}</div>
							</div>
							<div className="w-px h-10 bg-slate-200 hidden sm:block" />
							<div className="flex items-center gap-2.5">
								<div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-600 grid place-items-center flex-none"><FaRegClock size={15} /></div>
								<div>
									<div className="text-[15px] font-bold text-slate-700 leading-none whitespace-nowrap">{billableMin} min</div>
									<div className="text-[11.5px] text-slate-500 mt-0.5">watch time</div>
								</div>
							</div>
							<div className="flex items-center gap-2.5">
								<div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 grid place-items-center flex-none"><FaCoins size={15} /></div>
								<div>
									<div className="text-[15px] font-bold text-amber-700 leading-none whitespace-nowrap">≈ {perLang.toLocaleString()}</div>
									<div className="text-[11.5px] text-slate-500 mt-0.5">credits / language</div>
								</div>
							</div>
						</div>
						{isExisting && displayVideos.length > 0 && (
							<button
								type="button"
								onClick={allSelected ? deselectAll : selectAll}
								className="flex-none min-w-[150px] justify-center whitespace-nowrap inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary bg-primary/10 hover:bg-primary/15 transition rounded-full px-3.5 py-2"
							>
								<FaCheck size={11} />
								{allSelected ? 'Deselect all' : `Select all ${displayVideos.length} videos`}
							</button>
						)}
					</div>

					{isLoading ? (
						<div className="bg-white rounded-3xl border border-slate-200 shadow-small p-12 grid place-items-center"><Spinner color="primary" /></div>
					) : displayVideos.length === 0 ? (
						<div className="bg-white rounded-3xl border border-slate-200 shadow-small p-10 text-center">
							<div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 grid place-items-center mx-auto mb-3"><FaVideo size={18} /></div>
							<div className="text-[15px] font-semibold text-slate-800 mb-1.5">No videos match these filters</div>
							<div className="text-[12px] text-slate-500">Loosen a rule or two — try clearing the view count or date range.</div>
						</div>
					) : (
						<div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(215px, 1fr))' }}>
							{displayVideos.map(v => {
								const ex = existingById.get(v.id);
								return (
									<VideoCard
										key={v.id}
										v={v}
										onPlay={setPreview}
										selectable={isExisting}
										selected={isSelected(v.id)}
										locked={lockedIds.has(v.id)}
										badge={ex ? { label: STAGES[ex.stage]?.label || 'In automation', color: STAGES[ex.stage]?.color || '#9aa2b4' } : null}
										onToggle={isExisting ? () => toggle(v.id) : undefined}
									/>
								);
							})}
						</div>
					)}
					</>
					)}
				</div>
			</div>

			{/* YouTube preview modal — opens from a card's play button */}
			<Modal isOpen={!!preview} onClose={() => setPreview(null)} size="3xl" placement="center" classNames={{ base: 'rounded-3xl bg-black', closeButton: 'text-white/80 hover:bg-white/10 z-10' }}>
				<ModalContent>
					<ModalBody className="p-0 overflow-hidden">
						{preview && (
							<>
								<div className="w-full bg-black" style={{ aspectRatio: '16/9' }}>
									<iframe
										className="w-full h-full"
										src={`https://www.youtube.com/embed/${preview.id}?autoplay=1&rel=0`}
										title={preview.title}
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
										allowFullScreen
									/>
								</div>
								<div className="px-4 py-3 flex items-center gap-3">
									<div className="min-w-0 flex-1">
										<div className="text-[13.5px] font-semibold text-white truncate">{preview.title}</div>
										<div className="text-[12px] text-white/60">{fmtViews(preview.views)} views · {preview.ago}</div>
									</div>
									<a
										href={`https://www.youtube.com/watch?v=${preview.id}`}
										target="_blank"
										rel="noreferrer"
										className="shrink-0 text-[12.5px] font-semibold text-white/80 hover:text-white bg-white/10 rounded-full px-3 py-1.5"
									>
										Open on YouTube
									</a>
								</div>
							</>
						)}
					</ModalBody>
				</ModalContent>
			</Modal>
		</div>
	);
}
